import classNames from 'classnames';
import { FC } from 'react';
import Card from '../Card/Card';
import { CardEmphasis } from '../Card/Card.types';
import styles from './StatusTracker.module.css';
import { StatusTrackerProps, StatusTrackerStatus } from './StatusTracker.types';

export const cardEmphasisFromStatus = (status: StatusTrackerStatus) => {
  switch (status) {
    case StatusTrackerStatus.Scheduled:
      return CardEmphasis.Orange;
    case StatusTrackerStatus.Completed:
      return CardEmphasis.Green;
    case StatusTrackerStatus.Error:
      return CardEmphasis.Red;
    case StatusTrackerStatus.Pending:
    default:
      return CardEmphasis.Blue;
  }
};

const StatusTracker: FC<StatusTrackerProps> = ({
  status = StatusTrackerStatus.Pending,
  progress = 0,
  events,
  submitted,
  submittedLabel = 'Submitted:',
  finished,
  finishedLabel = 'Finished:',
  finishNote,
  footerNote,
}) => {
  const displayEvents = () => {
    const eventsFromProps: JSX.Element[] =
      events?.map((event) => (
        <div key={event.label} className={styles.progressEvent} style={{ left: `calc(${event.position}% - 24px)` }}>
          <div
            className={classNames(styles.progressCircle, {
              [styles[cardEmphasisFromStatus(status)]]: progress !== undefined && progress > event.position,
            })}
          />
          <div className={styles.progressLabel}>{event.label}</div>
          <div>{event.date}</div>
        </div>
      )) || [];
    return [
      // submitted (0%)
      <div className={styles.progressEvent} style={{ left: `calc(0% - 24px)` }} key="zeroPercent">
        <div
          className={classNames(styles.progressCircle, {
            [styles[cardEmphasisFromStatus(status)]]: progress !== undefined && progress > 0,
          })}
        />
        <div className={styles.progressLabel}>{submittedLabel}</div>
        <div>{submitted}</div>
      </div>,
      // events
      ...eventsFromProps,
      // finished (100%)
      <div
        className={styles.progressEvent}
        style={{ left: `calc(100% - 50px)`, minWidth: '120px' }}
        key="hundredPercent"
      >
        <div
          className={classNames(styles.progressCircle, {
            [styles[cardEmphasisFromStatus(status)]]: progress !== undefined && progress > 99,
          })}
        />
        {status === StatusTrackerStatus.Pending ? (
          finishNote
        ) : (
          <>
            <div className={styles.progressLabel}>{finishedLabel}</div>
            <div>{finished}</div>
          </>
        )}
      </div>,
      // current progress indicator (progress prop)
      <div
        className={styles.progressEvent}
        style={{ left: `calc(${progress}% - 24px)` }}
        data-testid="progress"
        key="currentProgress"
      >
        <div
          className={classNames(styles.progressCircle, styles.currentProgress, styles[cardEmphasisFromStatus(status)])}
        >
          <span />
        </div>
        <div className={styles.progressLabel} />
      </div>,
    ];
  };
  return (
    <Card emphasis={cardEmphasisFromStatus(status)} className={styles.columnStatus}>
      <div className={styles.container}>
        <div className={styles.status}>
          <div className={styles.statusLabel}>Status:</div>
          <div className={styles.statusText}>{status}</div>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.bar} />
          <div
            className={classNames(styles.filledBar, styles[cardEmphasisFromStatus(status)])}
            style={{ width: `${progress}%` }}
          />
          <div className={styles.events}>{displayEvents()}</div>
        </div>
      </div>
      {footerNote}
    </Card>
  );
};

export default StatusTracker;
